import {
  CaseImporterFieldResponse,
  CaseImporterResponse,
  OrganizationCaseImporterCreateRequest,
  OrganizationCaseImporterFieldCreateRequest,
  OrganizationCaseImporterResponse,
  OrganizationCaseImporterUpdateRequest,
  ProjectResponse,
} from 'api/core';
import {
  useCreateCaseImporter,
  useUpdateCaseImporter,
} from 'api/useCaseImportersApi';
import { useGetProjects } from 'api/useProjectsApi';
import { EntitySelectSingle } from 'components/Select/EntitySelectSingle';
import { buildQueryStateGetAll } from 'components/Table/useTableQueryState';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

interface CaseImporterFormProps {
  targetCaseImporter?: OrganizationCaseImporterResponse;
  allCaseImporterDefinitions: CaseImporterResponse[];
  onSuccess: () => void;
  onCancel: () => void;
  inDialog?: boolean;
}

export const CaseImporterForm = ({
  targetCaseImporter,
  allCaseImporterDefinitions,
  onSuccess,
  onCancel,
  inDialog,
}: CaseImporterFormProps) => {
  const { mutateAsync: createAsync, isPending: isPendingCreate } =
    useCreateCaseImporter();
  const { mutateAsync: updateAsync, isPending: isPendingUpdate } =
    useUpdateCaseImporter();

  const { data: allProjects } = useGetProjects(
    buildQueryStateGetAll<ProjectResponse>(),
    {
      includeSingleCaseProjects: false,
      includeMultiCaseProjects: true,
    }
  );

  const { register, handleSubmit, setValue, watch } = useForm<
    | OrganizationCaseImporterCreateRequest
    | OrganizationCaseImporterUpdateRequest
  >({
    defaultValues: {
      ...targetCaseImporter,
      caseImporterId: targetCaseImporter?.caseImporter.id,
    },
  });

  const caseImporterId = watch('caseImporterId');

  const [fields, setFields] = useState<
    (OrganizationCaseImporterFieldCreateRequest & CaseImporterFieldResponse)[]
  >([]);

  useEffect(() => {
    const definition = allCaseImporterDefinitions.find(
      (c) => c.id === caseImporterId
    );
    if (!definition) {
      setFields([]);
      return;
    }

    setFields(
      definition.fields.map((f) => ({
        ...f,
        caseImporterFieldId: f.id,
        value:
          targetCaseImporter?.fields?.find(
            (tf) => tf.caseImporterFieldId === f.id
          )?.value || f.defaultValue,
      }))
    );
  }, [allCaseImporterDefinitions, caseImporterId, targetCaseImporter]);

  const onSubmit = handleSubmit(async (result) => {
    const dto:
      | OrganizationCaseImporterCreateRequest
      | OrganizationCaseImporterUpdateRequest = {
      ...result,
      fields: fields.map((f) => ({
        caseImporterFieldId: f.caseImporterFieldId,
        value: f.value,
      })),
    };

    if (targetCaseImporter) {
      await updateAsync({
        id: targetCaseImporter?.id ?? '',
        organizationCaseImporterUpdateRequest:
          dto as OrganizationCaseImporterUpdateRequest,
      });
    } else {
      await createAsync({
        organizationCaseImporterCreateRequest:
          dto as OrganizationCaseImporterCreateRequest,
      });
    }
    onSuccess();
  });

  const onProjectSelected = (project: ProjectResponse | null) => {
    if (!project) return;
    setValue('projectId', project.id);
  };

  const onCaseImporterDefinitionSelected = (
    caseImporter: CaseImporterResponse | null
  ) => {
    if (!caseImporter) return;
    setValue('caseImporterId', caseImporter.id);
  };

  if (!allProjects) return null;

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <div className="form-control">
        <label className="label">
          <span className="label-text">Projekt</span>
        </label>
        <EntitySelectSingle<ProjectResponse>
          data={allProjects.data}
          renderFormat={(e) => <p>{e.name}</p>}
          onSelect={onProjectSelected}
          initialValue={allProjects.data.find(
            (p) => p.id === targetCaseImporter?.projectId
          )}
          searchPropertyKey="name"
          inDialog={inDialog}
          disabled={!!targetCaseImporter}
        />
        <input
          {...register('projectId', { required: true })}
          type="text"
          className="hidden"
        />
      </div>

      <div className="form-control">
        <label className="label">
          <span className="label-text">Integrationskilde</span>
        </label>
        <EntitySelectSingle<CaseImporterResponse>
          data={allCaseImporterDefinitions}
          renderFormat={(e) => <p>{e.name}</p>}
          onSelect={onCaseImporterDefinitionSelected}
          initialValue={allCaseImporterDefinitions.find(
            (c) => c.id === targetCaseImporter?.caseImporter.id
          )}
          searchPropertyKey="name"
          inDialog={inDialog}
          disabled={!!targetCaseImporter}
        />
        <input
          {...register('caseImporterId', { required: true })}
          type="text"
          className="hidden"
        />
      </div>

      {fields.map((f) => (
        <div key={f.id} className="form-control">
          <label className="label">
            <span className="label-text">{f.name}</span>
          </label>
          <input
            onChange={(e) => {
              setFields((prev) =>
                prev.map((field) =>
                  field.id === f.id
                    ? {
                        ...field,
                        value: e.target.value,
                      }
                    : field
                )
              );
            }}
            type={f.sensitive ? 'password' : 'text'}
            className="input input-bordered"
            value={f.value || ''}
            disabled={!f.overridableByOrganization}
          />
        </div>
      ))}

      <div className="flex justify-center space-x-4 pt-4">
        <button
          className="btn btn-primary"
          disabled={isPendingCreate || isPendingUpdate}
        >
          {targetCaseImporter ? 'Opdater' : 'Opret'}
        </button>
        {onCancel ? (
          <button type="button" className="btn" onClick={onCancel}>
            Annuller
          </button>
        ) : null}
      </div>
    </form>
  );
};
